import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cx from 'clsx';
import { sanitize } from 'dompurify';
import styled, { DefaultTheme } from 'styled-components';

import {
  FormState,
  FormValidation,
  useBookingContext,
} from '../../../../_common/context/BookingContext';
import ErrorMessage from '../../../../_common/fields/_common/errorMessage';
import Label from '../../../../_common/fields/_common/label';
import { useScopedTranslation } from '../../../../_common/utils/hooks/useScopedTranslation';

const ValueField = styled.div<ValueFieldProps>`
  display: flex;
  width: calc(100% - 3.25rem);
  height: calc(100% - 0.5rem);
  margin: 0.25rem 0;
  border: 1px solid
    ${({ hovering, initialErrors, theme }) =>
      initialErrors
        ? theme.colors.ERROR
        : hovering
        ? theme.colors.BORDER
        : theme.colors.TRANSPARENT};
  background-color: ${({ theme: { colors } }) => colors.GREY_7};
  opacity: ${({ editing }) => (editing ? '0.01' : '1')};
  transition: opacity 100ms ease-in, border 80ms ease-in;
  & .icon {
    color: ${({ initialErrors, theme }) =>
      initialErrors ? theme.colors.ERROR : theme.colors.BORDER};
    transition: opacity 80ms ease-in;
    opacity: ${({ hovering }) => (hovering ? '1' : '0.01')};
  }
  padding: ${({ multiline }) => (multiline ? '.75rem' : '1rem')} 1rem;
  align-items: ${({ multiline }) => (multiline ? 'flex-start' : 'center')};
  cursor: text;
  user-select: none;
  position: absolute;
  pointer-events: none;
  left: 2.75rem;
  top: 0;
`;
const Value = styled.span<ValueProps>`
  overflow: hidden;
  &:not(:first-child) {
    margin-top: 1rem;
  }
  ${({ theme }) => theme.breakPoints.medium} {
    &:not(:first-child) {
      margin: 0 0 0 0.3rem;
    }
  }
  text-overflow: ${({ multiline }) => (multiline ? 'none' : 'ellipsis')};
  white-space: ${({ multiline }) => (multiline ? 'pre-wrap' : 'nowrap')};
  display: block;
`;

export interface Props {
  icon?: any;
  className?: string;
  renderInputField: (
    startEditing: () => void,
    stopEditing: () => void,
    startHovering: () => void,
    stopHovering: () => void,
  ) => JSX.Element;
  alwaysShowValueField?: boolean;
  alwaysShowInputField?: boolean;
  multiline?: boolean;
  error?: string;
  legend?: string;
  touched?: boolean;
  label?: string;
  required?: boolean;
  requiredForQualification?: boolean;
  name: string | undefined;
  value: string | boolean | null;
  isGroup?: boolean;
}

interface ValueFieldProps {
  hovering: boolean;
  editing: boolean;
  theme: DefaultTheme;
  multiline?: boolean;
  initialErrors: boolean;
}

interface ValueProps {
  multiline?: boolean;
}

export default function CustomerFormField({
  alwaysShowInputField = false,
  alwaysShowValueField = false,
  className,
  error,
  icon,
  isGroup,
  label,
  legend,
  multiline,
  name,
  renderInputField,
  required,
  requiredForQualification,
  touched,
  value,
}: Props) {
  const { t } = useScopedTranslation();
  const [editing, setEditing] = useState(false);
  const [hovering, setHovering] = useState(false);
  const [{ customerFormState, customerFormValidation }] = useBookingContext();
  const Tag = legend !== undefined ? 'fieldset' : 'div';

  const computedLabel =
    label === undefined
      ? ''
      : sanitize(`${label[0].toUpperCase()}${label.substring(1)}`);

  const shouldShowError =
    !!value ||
    !!touched ||
    customerFormState === FormState.COLLAPSED ||
    customerFormValidation === FormValidation.FULL;

  const isRequired =
    customerFormValidation === FormValidation.FULL
      ? required
      : requiredForQualification;

  function startEditing() {
    if (!alwaysShowValueField) {
      setEditing(true);
    }
  }

  return (
    <Tag
      className={cx('relative flex flex-col mb-8 mt-2 w-full', className)}
      onClick={editing ? () => null : startEditing}
    >
      {label !== undefined && (
        <div className="flex items-end justify-between mb-2 ml-12 mr-2 h-auto">
          {legend !== undefined ? (
            <legend className="overflow-ellipsis m-0 overflow-hidden">
              <span dangerouslySetInnerHTML={{ __html: computedLabel }}></span>
            </legend>
          ) : (
            <Label
              className="overflow-ellipsis m-0 overflow-hidden"
              htmlFor={name}
            >
              <span dangerouslySetInnerHTML={{ __html: computedLabel }}></span>
            </Label>
          )}
          {customerFormState === FormState.EXPANDED && (
            <div className="ml-4 text-gray-500 text-sm">
              <>
                {isRequired &&
                  !(value && typeof value === 'string'
                    ? value.trim()
                    : value) &&
                  t('required')}
                {!isRequired &&
                  !value &&
                  (required ? t('requiredForBooking') : t('optional'))}
                {value && !error && (
                  <FontAwesomeIcon
                    className="-my-px w-5 h-5 text-green-700"
                    icon={['fas', 'check']}
                  />
                )}
              </>
            </div>
          )}
        </div>
      )}
      <div className="relative flex flex-row items-center px-2 py-1 max-w-full">
        <div className="relative flex items-center justify-center mr-4 w-5 min-w-5 h-5">
          {icon && (
            <FontAwesomeIcon
              className="-my-px w-5 h-5"
              icon={icon}
              fixedWidth
            />
          )}
        </div>
        <div className="w-full">
          {renderInputField(
            startEditing,
            () => setEditing(false),
            () => setHovering(true),
            () => setHovering(false),
          )}
          {!alwaysShowInputField && (
            <ValueField
              editing={editing}
              hovering={hovering || customerFormState === FormState.EXPANDED}
              initialErrors={
                customerFormState === FormState.EXPANDED &&
                shouldShowError &&
                !!error
              }
              multiline={multiline}
            >
              <div className="flex flex-1 flex-col justify-center w-full md:flex-row md:justify-start">
                {customerFormState === FormState.EXPANDED || value ? (
                  isGroup ? (
                    (value as string)
                      .split(' ')
                      .map((v) => <Value key={v}>{v}</Value>)
                  ) : (
                    <Value multiline={multiline}>{value}</Value>
                  )
                ) : (
                  <Value className="text-gray-600 italic">
                    {computedLabel}
                  </Value>
                )}
              </div>
              <FontAwesomeIcon
                className="icon self-center -my-px m-0 mr-4 w-5 h-5"
                icon={['fas', 'pencil-alt']}
              />
            </ValueField>
          )}
        </div>
      </div>
      <div
        className={cx(
          'ml-12 overflow-hidden',
          shouldShowError && !!error ? 'h-10 -mb-10' : 'h-0 mb-0',
        )}
      >
        {error?.split('|').map((e) => (
          <ErrorMessage id={`${name}-error`} key={e}>
            {e}
          </ErrorMessage>
        ))}
      </div>
    </Tag>
  );
}
